import { cn } from "@/lib/utils";

/** Grey block; pulses only when the viewer allows motion. */
function Bone({ className }: { className?: string }) {
  return <div className={cn("rounded-md bg-gray-100 motion-safe:animate-pulse", className)} />;
}

/**
 * Product detail placeholder — the layout of ProductDetailClient (gallery,
 * title block, price, sections, booking card / mobile bar). Shown by
 * ProductNavigationOverlay while a product navigation is still pending; the
 * route itself has no loading.tsx (its Suspense boundary hid the body from
 * crawlers and turned 404s into 200s).
 */
export default function ProductDetailSkeleton() {
  return (
    <div className="bg-white">
      <div className="hidden md:block px-6 sm:px-10 md:px-16 lg:px-20 xl:px-24 max-w-7xl mx-auto">
        <div className="flex items-center justify-end gap-2 py-3">
          <Bone className="w-10 h-10 rounded-full" />
          <Bone className="w-10 h-10 rounded-full" />
        </div>
      </div>

      <div className="mb-6 md:mb-12">
        <div className="md:hidden">
          <Bone className="w-full aspect-square rounded-none" />
        </div>
        <div className="hidden md:block px-6 sm:px-10 md:px-16 lg:px-20 xl:px-24 max-w-7xl mx-auto">
          <div className="grid grid-cols-4 grid-rows-2 gap-2 h-100 lg:h-125">
            <Bone className="col-span-2 row-span-2 rounded-none rounded-l-2xl" />
            <Bone className="rounded-none" />
            <Bone className="rounded-none rounded-tr-2xl" />
            <Bone className="rounded-none" />
            <Bone className="rounded-none rounded-br-2xl" />
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-10 md:px-16 lg:px-20 xl:px-24 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-8 lg:gap-12 pb-12">
        <div className="min-w-0 space-y-6">
          <div className="border-b pb-5 space-y-3">
            <div className="flex items-center gap-2">
              <Bone className="h-3.5 w-16" />
              <Bone className="h-3.5 w-20" />
            </div>
            <Bone className="h-6 w-4/5 md:h-8" />
            <Bone className="h-4 w-2/5" />
          </div>
          <div className="space-y-3">
            <Bone className="h-5 w-1/3" />
            <Bone className="h-3.5 w-full" />
            <Bone className="h-3.5 w-5/6" />
          </div>
          <Bone className="h-18 w-full rounded-xl" />
          <div className="space-y-2">
            <Bone className="h-6 w-32" />
            <Bone className="h-4 w-full" />
            <Bone className="h-4 w-4/5" />
            <Bone className="h-4 w-full" />
          </div>
        </div>

        <div className="hidden lg:block">
          <div className="p-6 border rounded-3xl shadow-xl bg-white">
            <Bone className="h-8 w-32 mb-6" />
            <div className="space-y-4">
              <Bone className="h-12 w-full rounded-xl" />
              <Bone className="h-12 w-full rounded-xl" />
            </div>
            <Bone className="h-14 w-full rounded-xl mt-6" />
          </div>
        </div>
      </div>

      <div className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t px-4 pt-3 pb-[max(0.75rem,var(--loc-safe-area-inset-bottom))]">
        <Bone className="h-12 w-full rounded-xl" />
      </div>
    </div>
  );
}
